import { useCallback, useRef } from 'react';
import { TOOLS } from '../constants/drawingTools';
import { getSnappedPoint, SNAP_THRESHOLD, lineSegmentIntersectsRect, calculateDistance, generateId, applyOrthogonalConstraint, getOrthogonalReferencePoint, recalculateShapeMeasurements, resolveShapeScaleProps } from '../utils/drawingUtils';
import { updateMultiPointShapeOnVertexDrag, insertVertexIntoShape, findNearestSegment } from '../utils/booleanOperations';


export const usePointerHandlers = (
    { // state
        selectedTool, isDrawing, currentShape, shapes, drawingStrokeColor, strokeWidth,
        stageScale, selectionRect, isOrthogonalModeActive, vertexEditInfo, snapIndicator, currentScaleProps,
        isRectangleMode, originalVertexPosition, isDraggingMode, isSnappingEnabled, isGridVisible,
        insertVertexMode, hoveredSegmentInfo, selectedShapeIds,
        dpi = 72,
        imageSize = { width: 0, height: 0 },
        selectedPointMarker = 'disk',
        measurementSystem = 'metric',
        allScales = [],
    },
    { // actions
        setIsDrawing, setCurrentShape, setShapes, setSelectionRect, setSelectedShapeIds,
        setSnapIndicator, setVisibleSnapPoints, setVertexEditInfo, setOriginalVertexPosition, finalizeAndResetDrawing, abortDrawing,
        setHoveredSegmentInfo, setInsertVertexMode,
        shapeAlreadyFinalizedRef,
    },
    stageRef // refs
) => {
    const panCheckRef = useRef({ start: null, empty: false });
    // Guard against double-fire from mouse+touch events on immediate-add tools
    const lastShapeAddTimestampRef = useRef(0);
    const SHAPE_ADD_DEBOUNCE_MS = 100;
    const handlePointerDown = useCallback((e) => {
        const stage = stageRef.current;
        if (!stage) return;

        const touches = e.evt.touches;
        const isMultiTouch = touches && touches.length > 1;

        if (e.evt.button === 1) {
            e.evt.preventDefault();
            stage.draggable(true);
            stage.startDrag();
            return;
        }

        if (e.evt.ctrlKey || e.evt.metaKey || isMultiTouch) {
            return; // Let stage handle panning
        }

        // Right-click during drawing finalizes it, during vertex edit cancels it
        if ((vertexEditInfo || isDrawing) && e.evt.button === 2) {
            if (isDrawing) {
                finalizeAndResetDrawing();
                return;
            }
            else if (vertexEditInfo && vertexEditInfo.isInserted) {
                // Cancel the current insert: restore the pre-insert shape (stay in insert mode)
                setShapes(prevShapes =>
                    prevShapes.map(shape => {
                        if (shape.id === vertexEditInfo.shapeId) {
                            const shapeScaleProps = resolveShapeScaleProps(vertexEditInfo.originalShape, currentScaleProps, allScales);
                            return recalculateShapeMeasurements(vertexEditInfo.originalShape, shapeScaleProps, dpi, imageSize);
                        }
                        return shape;
                    })
                );
                setVertexEditInfo(null);
                setOriginalVertexPosition(null);
                if (snapIndicator) setSnapIndicator(null);
                setVisibleSnapPoints([]);
                return;
            }
            else  {
                setShapes(prevShapes =>
                    prevShapes.map(shape => {
                        if (shape.id === vertexEditInfo.shapeId &&
                            (shape.type === TOOLS.POLYLINE || shape.type === TOOLS.AREA)) {
                            const updatedShape = updateMultiPointShapeOnVertexDrag(
                                shape,
                                vertexEditInfo.vertexIndex,
                                originalVertexPosition.x,
                                originalVertexPosition.y,
                                vertexEditInfo.isHole || false,
                                vertexEditInfo.holeIndex || 0
                            );
                            const shapeScaleProps = resolveShapeScaleProps(updatedShape, currentScaleProps, allScales);
                            return recalculateShapeMeasurements(updatedShape, shapeScaleProps, dpi, imageSize);
                        }
                        return shape;
                    })
                );
                setVertexEditInfo(null);
                setOriginalVertexPosition(null);
                if (snapIndicator) setSnapIndicator(null);
                setVisibleSnapPoints([]);
                return;
            }
        }

        if (insertVertexMode && !vertexEditInfo && e.evt.button === 2) {
            setInsertVertexMode(false);
            return;
        }

        if (e.target.getParent()?.className === 'Transformer') {
            return;
        }

        if (selectedTool === TOOLS.SELECT) {
            if (vertexEditInfo && vertexEditInfo.isInserted && (e.evt.button === 0 || e.evt.button === undefined)) {
                setVertexEditInfo(null);
                setOriginalVertexPosition(null);
                if (snapIndicator) setSnapIndicator(null);
                setVisibleSnapPoints([]);
                return;
            }
            if (insertVertexMode && !vertexEditInfo && (e.evt.button === 0 || e.evt.button === undefined)) {
                const pos = stage.getRelativePointerPosition();
                const shape = shapes.find(s => s.id === selectedShapeIds[0]);
                if (shape && (shape.type === TOOLS.POLYLINE || shape.type === TOOLS.AREA)) {
                    const { segmentIndex, projectedPoint, distance, vertexDistance, isHole, holeIndex } = findNearestSegment(shape, pos.x, pos.y);
                    const threshold = SNAP_THRESHOLD / stageScale;
                    if (segmentIndex !== -1 && distance <= threshold && vertexDistance > threshold) {
                        const relX = projectedPoint.x - shape.x;
                        const relY = projectedPoint.y - shape.y;
                        setShapes(prevShapes => prevShapes.map(s => {
                            if (s.id !== shape.id) return s;
                            const inserted = insertVertexIntoShape(s, segmentIndex, relX, relY, isHole, holeIndex);
                            const shapeScaleProps = resolveShapeScaleProps(inserted, currentScaleProps, allScales);
                            return recalculateShapeMeasurements(inserted, shapeScaleProps, dpi, imageSize);
                        }));
                        setVertexEditInfo({ shapeId: shape.id, vertexIndex: segmentIndex + 1, isHole, holeIndex, isInserted: true, originalShape: { ...shape } });
                        setOriginalVertexPosition({ x: relX, y: relY }); // RELATIVE, matching existing cancel expectation
                        setHoveredSegmentInfo(null);
                        return;
                    }
                }
                return; // swallow the click while in insert mode
            }
            const clickedOnEmpty = e.target === stage;
            if (clickedOnEmpty) {
                // Clear vertex edit mode when clicking on empty stage with LEFT button
                // (Right-click abort is handled earlier in this function)
                if (vertexEditInfo && e.evt.button === 0) {
                    setVertexEditInfo(null);
                    setOriginalVertexPosition(null);
                    if (snapIndicator) setSnapIndicator(null);
                    setVisibleSnapPoints([]);
                }
                
                // LEFT BUTTON drag begins a pan
                if (e.evt.button === 0 || e.evt.button === undefined) {
                    panCheckRef.current.start = stage.getPointerPosition()
                        || (e.evt.changedTouches?.[0]
                            ? { x: e.evt.changedTouches[0].clientX, y: e.evt.changedTouches[0].clientY }
                            : null);
                    panCheckRef.current.empty = true;   // <-- important
                }
                // RIGHT BUTTON drag begins a selection rectangle (marquee select)
                if (e.evt.button === 2) {
                    // Prevent default context menu from showing when starting a selection rect
                    try { e.evt.preventDefault(); } catch (ex) { /* defensive */ }
                    const pos = stage.getRelativePointerPosition();

                    if (pos) {
                        setSelectionRect({ x: pos.x, y: pos.y, width: 0, height: 0, startX: pos.x, startY: pos.y, visible: true });
                        setIsDrawing(true);
                        if (!e.evt.shiftKey) {
                            // setSelectedShapeIds([]);
                        }
                    }
                } else {
                    // Left click (button===0) on empty stage should be a pan if stage is draggable.
                    // We don't set drawing state so stage draggable will allow panning.
                    if (e.evt.button === 0) {
                        // panCheckRef.current.start = stage.getRelativePointerPosition();
                    }

                }
            } else {
                // Clicked on a shape (not empty stage)
                let shapeGroup = e.target;

                panCheckRef.current.empty = false;
                panCheckRef.current.start = null;

                // If we clicked on a child element within a Group, get the parent Group
                // Check if target has a parent and if that parent is a Group
                const parent = e.target.getParent();
                if (parent && parent.getClassName && parent.getClassName() === 'Group') {
                    shapeGroup = parent;
                }

                // Get the ID - try as a method first (Konva shapes), then as attribute
                let shapeId;
                if (typeof shapeGroup.id === 'function') {
                    shapeId = shapeGroup.id();
                } else {
                    shapeId = shapeGroup.attrs?.id;
                }

                // Only select/deselect shapes using the left mouse button or touch (button undefined)
                if (shapeId && (e.evt.button === 0 || e.evt.button === undefined)) {
                    const shiftPressed = e.evt.shiftKey;
                    // Prevent stage dragging during selection click
                    stage.draggable(false);

                    // Use a functional update to get the latest selection state
                    setSelectedShapeIds(prevSelectedIds => {
                        const currentSelection = new Set(prevSelectedIds);

                        if (shiftPressed) {
                            // Toggle selection for the clicked shape
                            if (currentSelection.has(shapeId)) {
                                currentSelection.delete(shapeId);
                            } else {
                                currentSelection.add(shapeId);
                            }
                        } else {
                            // If the shape is not already selected, clear others and select it.
                            // If it is already selected, do nothing to allow dragging to begin.
                            if (!currentSelection.has(shapeId)) {
                                currentSelection.clear();
                                currentSelection.add(shapeId);
                            }
                        }

                        return Array.from(currentSelection);
                    });

                }
            }
            return; // Important: End execution for SELECT tool here
        }

        setIsDrawing(true);
        let pos = stage.getRelativePointerPosition();
        if (!pos) { setIsDrawing(false); return; }

        // For annotation tools, ensure stage is not draggable to prevent panning
        const isAnnotationTool = [TOOLS.PEN, TOOLS.RECTANGLE, TOOLS.CIRCLE, TOOLS.LINE, TOOLS.MEASURE, TOOLS.COTATION, TOOLS.ARROW].includes(selectedTool);
        if (isAnnotationTool && stage.draggable()) {
            stage.draggable(false);
        }

        if ((selectedTool === TOOLS.POLYLINE || selectedTool === TOOLS.POLYGON || selectedTool === TOOLS.COTATION || selectedTool === TOOLS.ARROW) && pos) {
            const shapeBeingDrawn = (currentShape && (currentShape.type === TOOLS.POLYLINE || currentShape.type === TOOLS.POLYGON || currentShape.type === TOOLS.COTATION || currentShape.type === TOOLS.ARROW)) ? currentShape : null;
            if (isOrthogonalModeActive && shapeBeingDrawn && shapeBeingDrawn.points.length >= 4) {
                const points = shapeBeingDrawn.points;
                const numPoints = points.length;
                const lastFixedX = points[numPoints - 4];
                const lastFixedY = points[numPoints - 3];

                if (Math.abs(pos.x - lastFixedX) > Math.abs(pos.y - lastFixedY)) {
                    pos.y = lastFixedY;
                } else {
                    pos.x = lastFixedX;
                }
            }
            const snapResult = getSnappedPoint(pos, shapeBeingDrawn, shapes, stageScale, SNAP_THRESHOLD, null, null, imageSize, isSnappingEnabled, isGridVisible, currentScaleProps, dpi, measurementSystem);
            pos = snapResult.snappedPoint;
            setVisibleSnapPoints(isSnappingEnabled ? snapResult.visibleSnapPoints : []);
        }

        const newShapeId = generateId();
        const commonProps = {
            id: newShapeId,
            stroke: drawingStrokeColor,
            strokeWidth: strokeWidth,
            rotation: 0,
            x: 0,
            y: 0,
            visible: true,
        };

        switch (selectedTool) {
            case TOOLS.PEN:
                if (Date.now() - lastShapeAddTimestampRef.current < SHAPE_ADD_DEBOUNCE_MS) break;
                lastShapeAddTimestampRef.current = Date.now();
                setShapes(prev => [...prev, { ...commonProps, type: TOOLS.PEN, points: [pos.x, pos.y] }]);
                break;
            case TOOLS.RECTANGLE:
                setCurrentShape({ ...commonProps, type: TOOLS.RECTANGLE, x: pos.x, y: pos.y, width: 1, height: 1, startX: pos.x, startY: pos.y });
                break;
            case TOOLS.CIRCLE:
                setCurrentShape({ ...commonProps, type: TOOLS.CIRCLE, x: pos.x, y: pos.y, radius: 1, startX: pos.x, startY: pos.y });
                break;
            case TOOLS.LINE:
            case TOOLS.MEASURE:
                setCurrentShape({ ...commonProps, type: selectedTool, points: [pos.x, pos.y, pos.x, pos.y], startX: pos.x, startY: pos.y, distance: 0 });
                break;
            case TOOLS.COTATION:
            case TOOLS.ARROW:
                if (currentShape && currentShape.type === selectedTool) {
                    const [startX, startY] = currentShape.points;
                    const endX = pos.x;
                    const endY = pos.y;

                    if (startX !== endX || startY !== endY) {
                        const { startX: sX, startY: sY, ...finalShape } = currentShape;
                        const shapeToAdd = {
                            ...finalShape,
                            x: startX,
                            y: startY,
                            points: [0, 0, endX - startX, endY - startY],
                            distance: (finalShape.type === TOOLS.COTATION) ? calculateDistance(0, 0, endX - startX, endY - startY) : null,
                            originalDPI: dpi,
                            storedDPI: dpi,
                            ...(finalShape.type === TOOLS.ARROW ? {
                                text: (finalShape.id.slice(-10) + '\n---\nBody text here...'),
                                pendingTextEdit: true
                            } : {})
                        };
                        setShapes(prev => [...prev, shapeToAdd]);
                        shapeAlreadyFinalizedRef.current = true;
                    }
                    setIsDrawing(false);
                    setCurrentShape(null);
                    if (snapIndicator) setSnapIndicator(null);
                    setVisibleSnapPoints([]);
                } else {
                    setCurrentShape({
                        ...commonProps,
                        type: selectedTool,
                        points: [pos.x, pos.y, pos.x, pos.y],
                        startX: pos.x,
                        startY: pos.y,
                        distance: 0
                    });
                }
                break;
            case TOOLS.POINT:
                if (Date.now() - lastShapeAddTimestampRef.current < SHAPE_ADD_DEBOUNCE_MS) break;
                lastShapeAddTimestampRef.current = Date.now();
                setShapes(prev => [...prev, { ...commonProps, type: TOOLS.POINT, x: pos.x, y: pos.y, radius: strokeWidth / 2 + 1, pointMarker: selectedPointMarker || 'disk' }]);
                setIsDrawing(false);
                break;
            case TOOLS.TEXT:
                if (Date.now() - lastShapeAddTimestampRef.current < SHAPE_ADD_DEBOUNCE_MS) break;
                lastShapeAddTimestampRef.current = Date.now();
                setShapes(prev => [...prev, { ...commonProps, type: TOOLS.TEXT, x: pos.x, y: pos.y, text: 'Text\n---\nDouble-click to edit...', pendingTextEdit: true }]);
                setIsDrawing(false);
                break;
            case TOOLS.POLYLINE:
                if (currentShape && currentShape.type === TOOLS.POLYLINE) {
                    const prevPoints = currentShape.points;
                    const numPrevPoints = prevPoints.length;
                    const segmentStartX = prevPoints[numPrevPoints - 4];
                    const segmentStartY = prevPoints[numPrevPoints - 3];
                    const finalizedSegmentDistance = calculateDistance(segmentStartX, segmentStartY, pos.x, pos.y);

                    setCurrentShape(prev => {
                        if (!prev || prev.type !== TOOLS.POLYLINE) return prev;
                        const updatedPoints = [...prev.points.slice(0, -2), pos.x, pos.y, pos.x, pos.y];
                        const updatedDistances = [...(prev.segmentDistances || []).slice(0, -1), finalizedSegmentDistance, 0];
                        return { ...prev, points: updatedPoints, segmentDistances: updatedDistances };
                    });
                } else {
                    setCurrentShape({
                        ...commonProps,
                        type: TOOLS.POLYLINE,
                        points: [pos.x, pos.y, pos.x, pos.y],
                        segmentDistances: [0],
                    });
                }
                break;
            case TOOLS.POLYGON:
                if (isRectangleMode) {
                    // Rectangle mode (two-click rectangle -> polygon)
                    if (
                        currentShape &&
                        currentShape.type === TOOLS.POLYGON &&
                        currentShape.isRectangleMode
                    ) {
                        // Second click: finalize rectangle
                        const startX = currentShape.rectangleStart.x;
                        const startY = currentShape.rectangleStart.y;
                        const endX = pos.x;
                        const endY = pos.y;
                        const w = endX - startX;
                        const h = endY - startY;

                        if (Math.abs(w) < 1 && Math.abs(h) < 1) {
                            // Too small: cancel
                            setIsDrawing(false);
                            setCurrentShape(null);
                            break;
                        }

                        const relPoints = [0, 0, w, 0, w, h, 0, h];
                        const d1 = Math.abs(w);
                        const d2 = Math.abs(h);

                        const finalShape = {
                            ...currentShape,
                            points: relPoints,
                            segmentDistances: [d1, d2, d1, d2],
                            x: startX,
                            y: startY,
                            isAxisAlignedRectangle: true // <-- added flag
                        };

                        setShapes(prev => [...prev, finalShape]);
                        setIsDrawing(false);
                        setCurrentShape(null);
                    } else {
                        // First click: initialize rectangle preview
                        setCurrentShape({
                            ...commonProps,
                            type: TOOLS.POLYGON,
                            isRectangleMode: true,
                            rectangleStart: { x: pos.x, y: pos.y },
                            x: pos.x,
                            y: pos.y,
                            // Relative points (start at 0,0)
                            points: [0, 0, 0, 0, 0, 0, 0, 0],
                            previewWidth: 0,
                            previewHeight: 0,
                            segmentDistances: [0, 0, 0, 0] // <-- added
                        });
                    }
                } else if (currentShape && currentShape.type === TOOLS.POLYGON) {
                    const prevPoints = currentShape.points;
                    const numPrevPoints = prevPoints.length;
                    const segmentStartX = prevPoints[numPrevPoints - 4];
                    const segmentStartY = prevPoints[numPrevPoints - 3];
                    const finalizedSegmentDistance = calculateDistance(segmentStartX, segmentStartY, pos.x, pos.y);

                    setCurrentShape(prev => {
                        if (!prev || prev.type !== TOOLS.POLYGON) return prev;
                        const updatedPoints = [...prev.points.slice(0, -2), pos.x, pos.y, pos.x, pos.y];
                        const updatedDistances = [...(prev.segmentDistances || []).slice(0, -2), finalizedSegmentDistance, 0, 0];
                        return { ...prev, points: updatedPoints, segmentDistances: updatedDistances };
                    });
                } else {
                    setCurrentShape({
                        ...commonProps,
                        type: TOOLS.POLYGON,
                        points: [pos.x, pos.y, pos.x, pos.y],
                        segmentDistances: [0, 0],
                    });
                }
                break;
            default:
                break;
        }
    }, [
        selectedTool, stageRef, drawingStrokeColor, strokeWidth, currentShape, setCurrentShape,
        setShapes, setIsDrawing, shapes, stageScale, setSelectionRect, setSelectedShapeIds,
        isOrthogonalModeActive, vertexEditInfo, setVertexEditInfo, setOriginalVertexPosition,
        isRectangleMode, selectedPointMarker, finalizeAndResetDrawing, originalVertexPosition,
        currentScaleProps, allScales, dpi, imageSize, snapIndicator, setSnapIndicator,
        setVisibleSnapPoints, isSnappingEnabled, isGridVisible, measurementSystem,
        insertVertexMode, setHoveredSegmentInfo, setInsertVertexMode
    ]);

    const handlePointerMove = useCallback((e) => {
        const stage = stageRef.current;
        if (!stage) return;

        const touches = e.evt.touches;
        if (touches && touches.length > 1) return;

        let pos = stage.getRelativePointerPosition();

        if (insertVertexMode && !vertexEditInfo && selectedTool === TOOLS.SELECT) {
            const shape = shapes.find(s => s.id === selectedShapeIds[0]);
            if (shape && (shape.type === TOOLS.POLYLINE || shape.type === TOOLS.AREA) && pos) {
                const { segmentIndex, projectedPoint, distance, vertexDistance, isHole, holeIndex } = findNearestSegment(shape, pos.x, pos.y);
                const threshold = SNAP_THRESHOLD / stageScale;
                if (segmentIndex !== -1 && distance <= threshold && vertexDistance > threshold && projectedPoint) {
                    setHoveredSegmentInfo({ shapeId: shape.id, segmentIndex, x: projectedPoint.x, y: projectedPoint.y, isHole, holeIndex });
                } else {
                    setHoveredSegmentInfo(null);
                }
            } else {
                setHoveredSegmentInfo(null);
            }
            return;
        }

        if (vertexEditInfo) {
            if (pos) {
                if (isOrthogonalModeActive) {
                    const referencePos = getOrthogonalReferencePoint(selectedTool, currentShape, vertexEditInfo, shapes);
                    pos = applyOrthogonalConstraint(pos, referencePos, isOrthogonalModeActive);
                }

                const snapResult = getSnappedPoint(
                    pos, null, shapes, stageScale, SNAP_THRESHOLD,
                    vertexEditInfo.shapeId, vertexEditInfo.vertexIndex, imageSize, isSnappingEnabled, isGridVisible,
                    currentScaleProps, dpi, measurementSystem
                );
                const snappedPos = snapResult.snappedPoint;
                setVisibleSnapPoints(isSnappingEnabled ? snapResult.visibleSnapPoints : []);

                if (snappedPos.x !== pos.x || snappedPos.y !== pos.y) {
                    setSnapIndicator({ x: snappedPos.x, y: snappedPos.y, visible: true });
                } else {
                    if (snapIndicator) setSnapIndicator(null);
                }

                pos = snappedPos;

                 setShapes(prevShapes =>
                    prevShapes.map(shape => {
                        if (shape.id === vertexEditInfo.shapeId &&
                            (
                                shape.type === TOOLS.POLYLINE || 
                                shape.type === TOOLS.AREA ||
                                shape.type === TOOLS.MEASURE ||
                                shape.type === TOOLS.COTATION ||
                                shape.type === TOOLS.ARROW
                            )) {

                            const newRelX = pos.x - shape.x;
                            const newRelY = pos.y - shape.y;

                            const updatedShape = updateMultiPointShapeOnVertexDrag(
                                shape, vertexEditInfo.vertexIndex, newRelX, newRelY,
                                vertexEditInfo.isHole || false, vertexEditInfo.holeIndex || 0
                            );
                            const shapeScaleProps = resolveShapeScaleProps(updatedShape, currentScaleProps, allScales);
                            return recalculateShapeMeasurements(updatedShape, shapeScaleProps, dpi, imageSize);
                        }
                        return shape;
                    })
                );
            }
            return;
        }

        if (!pos) {
            if (snapIndicator) setSnapIndicator(null);
            setVisibleSnapPoints([]);
            return;
        }

        if (isOrthogonalModeActive && isDrawing) {
            const referencePos = getOrthogonalReferencePoint(selectedTool, currentShape, vertexEditInfo, shapes);
            pos = applyOrthogonalConstraint(pos, referencePos, isOrthogonalModeActive);
        }

        // Always check for snapping, but handle indicator visibility based on whether a snap occurred.
        const shapeBeingDrawn = isDrawing ? currentShape : null;
        const snapResult = getSnappedPoint(pos, shapeBeingDrawn, shapes, stageScale, SNAP_THRESHOLD, null, null, imageSize, isSnappingEnabled, isGridVisible, currentScaleProps, dpi, measurementSystem);
        const snappedPos = snapResult.snappedPoint;
        setVisibleSnapPoints(isSnappingEnabled ? snapResult.visibleSnapPoints : []);

        if (snappedPos.x !== pos.x || snappedPos.y !== pos.y) {
            setSnapIndicator({ x: snappedPos.x, y: snappedPos.y, visible: true });
        } else {
            if (snapIndicator) setSnapIndicator(null);
        }
        pos = snappedPos;


        if (selectedTool === TOOLS.SELECT) {
            if (!isDrawing || !selectionRect || !selectionRect.visible) return;
        } else {
            if (!isDrawing) return;
        }

        switch (selectedTool) {
            case TOOLS.SELECT: {
                if (isDrawing && selectionRect && selectionRect.visible) {
                    if (!pos) return;
                    const newWidth = pos.x - selectionRect.startX;
                    const newHeight = pos.y - selectionRect.startY;
                    setSelectionRect(prev => ({
                        ...prev,
                        x: newWidth < 0 ? pos.x : prev.startX,
                        y: newHeight < 0 ? pos.y : prev.startY,
                        width: Math.abs(newWidth),
                        height: Math.abs(newHeight)
                    }));
                }
                break;
            }
            case TOOLS.PEN: {
                setShapes(prevShapes => {
                    const lastShape = prevShapes[prevShapes.length - 1];
                    if (lastShape && lastShape.type === TOOLS.PEN) {
                        const newPoints = lastShape.points.concat([pos.x, pos.y]);
                        return [...prevShapes.slice(0, -1), { ...lastShape, points: newPoints }];
                    }
                    return prevShapes;
                });
                break;
            }
            case TOOLS.RECTANGLE: {
                if (!currentShape) return;
                const newWidth = pos.x - currentShape.startX;
                const newHeight = pos.y - currentShape.startY;
                setCurrentShape(prev => ({
                    ...prev,
                    x: newWidth < 0 ? pos.x : currentShape.startX,
                    y: newHeight < 0 ? pos.y : currentShape.startY,
                    width: Math.max(20, Math.abs(newWidth)),
                    height: Math.max(20, Math.abs(newHeight))
                }));
                break;
            }
            case TOOLS.CIRCLE: {
                if (!currentShape) return;
                const dx = pos.x - currentShape.startX;
                const dy = pos.y - currentShape.startY;
                const radius = Math.max(1, Math.sqrt(dx * dx + dy * dy));
                setCurrentShape(prev => ({ ...prev, radius: radius }));
                break;
            }
            case TOOLS.LINE:
            case TOOLS.MEASURE:
            case TOOLS.COTATION:
            case TOOLS.ARROW: {
                if (!currentShape) return;
                const [startX, startY] = currentShape.points;
                const updatedPoints = [startX, startY, pos.x, pos.y];
                let distance = 0;
                if (selectedTool === TOOLS.MEASURE || selectedTool === TOOLS.COTATION) {
                    distance = calculateDistance(startX, startY, pos.x, pos.y);
                }
                setCurrentShape(prev => ({ ...prev, points: updatedPoints, distance: distance }));
                break;
            }
            case TOOLS.POLYLINE: {
                if (!currentShape || currentShape.type !== TOOLS.POLYLINE) return;
                const existingPoints = currentShape.points;
                const numPoints = existingPoints.length;
                if (numPoints < 4) return;

                const lastFixedX = existingPoints[numPoints - 4];
                const lastFixedY = existingPoints[numPoints - 3];
                const previewDistance = calculateDistance(lastFixedX, lastFixedY, pos.x, pos.y);

                const updatedPreviewPoints = existingPoints.slice(0, -2).concat([pos.x, pos.y]);
                setCurrentShape(prev => ({
                    ...prev,
                    points: updatedPreviewPoints,
                    segmentDistances: [...(prev.segmentDistances || []).slice(0, -1), previewDistance]
                }));
                break;
            }
            case TOOLS.POLYGON: {
                if (!currentShape || currentShape.type !== TOOLS.POLYGON) return;

                // Rectangle mode preview
                if (isRectangleMode && currentShape.isRectangleMode) {
                    const startX = currentShape.rectangleStart.x;
                    const startY = currentShape.rectangleStart.y;
                    const w = pos.x - startX;
                    const h = pos.y - startY;

                    // Only update if dimensions changed
                    if (w !== currentShape.previewWidth || h !== currentShape.previewHeight) {
                        const relPoints = [0, 0, w, 0, w, h, 0, h];
                        const d1 = Math.abs(w);
                        const d2 = Math.abs(h);
                        // Order matches point sequence: top, right, bottom, left
                        const segmentDistances = [d1, d2, d1, d2];

                        setCurrentShape(prev => ({
                            ...prev,
                            points: relPoints,
                            previewWidth: w,
                            previewHeight: h,
                            segmentDistances
                        }));
                    }
                    break;
                }

                const existingPoints = currentShape.points;
                const numPoints = existingPoints.length;
                if (numPoints < 4) return;

                const lastFixedX = existingPoints[numPoints - 4];
                const lastFixedY = existingPoints[numPoints - 3];
                const firstX = existingPoints[0];
                const firstY = existingPoints[1];

                const previewSegmentDistance = calculateDistance(lastFixedX, lastFixedY, pos.x, pos.y);
                const closingSegmentDistance = calculateDistance(pos.x, pos.y, firstX, firstY);

                const updatedPreviewPoints = existingPoints.slice(0, -2).concat([pos.x, pos.y]);

                setCurrentShape(prev => ({
                    ...prev,
                    points: updatedPreviewPoints,
                    segmentDistances: [
                        ...(prev.segmentDistances || []).slice(0, -2),
                        previewSegmentDistance,
                        closingSegmentDistance,
                    ],
                }));
                break;
            }
            default: break;
        }
    }, [
        isDrawing, selectedTool, stageRef, currentShape, setCurrentShape, shapes, stageScale,
        selectionRect, setSelectionRect, isOrthogonalModeActive, snapIndicator, vertexEditInfo, setShapes,
        setSnapIndicator, currentScaleProps, isRectangleMode,
        insertVertexMode, setHoveredSegmentInfo
    ]);

    const handlePointerUp = useCallback((e) => {
        // Re-enable stage dragging after any pointer up event
        const stage = stageRef.current;
        if (stage) {
            // Re-enable stage dragging based on current tool
            const canDragStage = (selectedTool === TOOLS.SELECT && !isDrawing) || selectedTool === TOOLS.NAVIGATION;
            stage.draggable(canDragStage);
        }

        if (e && e.evt && e.evt.button === 1) {
            return; // Ignore middle click UP so we don't finalize shapes
        }
        // Unselect on empty-stage click, but not after a pan/drag
        if (
            selectedTool === TOOLS.SELECT &&
            (e.evt.button === 0 || e.evt.button === undefined) &&
            panCheckRef.current.start &&
            panCheckRef.current.empty
        ) {
            const end = stage.getPointerPosition()
                || (e.evt.changedTouches?.[0]
                    ? { x: e.evt.changedTouches[0].clientX, y: e.evt.changedTouches[0].clientY }
                    : null);
            const start = panCheckRef.current.start;

            panCheckRef.current.start = null;
            panCheckRef.current.empty = false;

            const dx = (end?.x ?? start.x) - start.x;
            const dy = (end?.y ?? start.y) - start.y;
            const moved = Math.hypot(dx, dy);
           
           
            const PAN_THRESHOLD = 10;

            // Only clear on click, not pan
            if (moved <= PAN_THRESHOLD  && e.evt.shiftKey !== true) {
                setSelectedShapeIds([]);
            }
            return;
        }

        if (
            selectedTool === TOOLS.POLYLINE ||
            selectedTool === TOOLS.POLYGON ||
            selectedTool === TOOLS.COTATION ||
            selectedTool === TOOLS.ARROW
        ) {
            return;
        }

        if (e && selectedTool === TOOLS.SELECT && isDrawing && selectionRect && selectionRect.visible) {
            setIsDrawing(false);
            const stage = stageRef.current;
            if (stage && selectionRect.width > 0 && selectionRect.height > 0) {
                const selRectKonva = {
                    x: selectionRect.x, y: selectionRect.y,
                    width: selectionRect.width, height: selectionRect.height,
                };
                const intersectedIds = [];
                shapes.forEach(shape => {
                    const mainNode = stage.findOne('#' + shape.id);
                    if (mainNode) {
                        let nodeForIntersection = mainNode;
                        if (typeof mainNode.findOne === 'function') {
                            // Use hasName instead of CSS class selector
                            const children = mainNode.getChildren();
                            const visualShapeNode = children.find(child =>
                                child.hasName && child.hasName('visual_shape')
                            );
                            if (visualShapeNode) {
                                nodeForIntersection = visualShapeNode;
                            }
                        }

                        const nodeRect = nodeForIntersection.getClientRect({ relativeTo: stage });
                        let intersects = false;
                        if (nodeRect) {
                            const aabbIntersects = selRectKonva.x < nodeRect.x + nodeRect.width &&
                                selRectKonva.x + selRectKonva.width > nodeRect.x &&
                                selRectKonva.y < nodeRect.y + nodeRect.height &&
                                selRectKonva.y + selRectKonva.height > nodeRect.y;

                            if (aabbIntersects) {
                                if (shape.type === TOOLS.POLYLINE || shape.type === TOOLS.LINE || shape.type === TOOLS.MEASURE || shape.type === TOOLS.COTATION || shape.type === TOOLS.ARROW) {
                                    intersects = false;
                                    const points = shape.points;
                                    for (let i = 0; i < points.length - 2; i += 2) {
                                        const p1 = { x: shape.x + points[i], y: shape.y + points[i + 1] };
                                        const p2 = { x: shape.x + points[i + 2], y: shape.y + points[i + 3] };
                                        if (lineSegmentIntersectsRect(p1, p2, selRectKonva)) {
                                            intersects = true;
                                            break;
                                        }
                                    }
                                } else {
                                    intersects = true;
                                }
                            }
                        }
                        if (intersects) {
                            intersectedIds.push(shape.id);
                        }
                    }
                });

                const shiftPressed = e.evt.shiftKey;
                if (shiftPressed) {
                    setSelectedShapeIds(prevSelectedIds => {
                        const newSelectedIds = new Set(prevSelectedIds);
                        intersectedIds.forEach(id => {
                            if (newSelectedIds.has(id)) {
                                newSelectedIds.delete(id);
                            } else {
                                newSelectedIds.add(id);
                            }
                        });
                        return Array.from(newSelectedIds);
                    });
                } else {
                    setSelectedShapeIds(intersectedIds);
                }
            }
            setSelectionRect(null);
            return;
        }
        setIsDrawing(false);

        let shapeToAdd = null;
        if (selectedTool === TOOLS.PEN) {
            // Pen shape is already in `shapes` state
        } else if (currentShape) {
            if (currentShape.type === TOOLS.RECTANGLE && Math.abs(currentShape.width) >= 50 && Math.abs(currentShape.height) >= 50) {
                const { startX, startY, ...finalShape } = currentShape;
                shapeToAdd = { ...finalShape, text: (finalShape.id.slice(-10) + '\n---\nBody text here...'), pendingTextEdit: true };
            } else if (currentShape.type === TOOLS.CIRCLE && currentShape.radius > 0) {
                const { startX, startY, ...finalShape } = currentShape;
                shapeToAdd = { ...finalShape, x: startX, y: startY };
            } else if ((currentShape.type === TOOLS.LINE || currentShape.type === TOOLS.MEASURE || currentShape.type === TOOLS.COTATION || currentShape.type === TOOLS.ARROW) && currentShape.points.length === 4) {
                const [x1, y1, x2, y2] = currentShape.points;
                if (x1 !== x2 || y1 !== y2) {
                    const { startX, startY, ...finalShape } = currentShape;
                    shapeToAdd = {
                        ...finalShape, x: x1, y: y1,
                        points: [0, 0, x2 - x1, y2 - y1],
                        distance: (finalShape.type === TOOLS.MEASURE || finalShape.type === TOOLS.COTATION) ? calculateDistance(0, 0, x2 - x1, y2 - y1) : null,
                    };
                }
            }
        }

        if (shapeToAdd) {
            setShapes(prev => [...prev, shapeToAdd]);
            // Signal that we already finalized this shape. If selectTool() calls
            // finalizeAndResetDrawing before React flushes, it should skip.
            shapeAlreadyFinalizedRef.current = true;
        }
        setCurrentShape(null);

    }, [
        isDrawing, selectedTool, currentShape, strokeWidth, setShapes, setCurrentShape,
        setIsDrawing, selectionRect, setSelectionRect, shapes, stageRef, setSelectedShapeIds
    ]);

    const handleDoubleClick = useCallback(() => {
        if (isRectangleMode) return; // Rectangle mode finalizes on second click
        if (selectedTool === TOOLS.POLYGON && isDrawing && currentShape) {
            const finalPoints = currentShape.points.slice(0, -2);

            if (finalPoints.length < 6) {
                setIsDrawing(false);
                setCurrentShape(null);
                return;
            }

            const firstX = finalPoints[0];
            const firstY = finalPoints[1];
            const finalDistances = currentShape.segmentDistances.slice(0, -2);
            const lastX = finalPoints[finalPoints.length - 2];
            const lastY = finalPoints[finalPoints.length - 1];
            const closingDistance = calculateDistance(lastX, lastY, firstX, firstY);
            finalDistances.push(closingDistance);

            const relativePoints = finalPoints.map((p, i) => (i % 2 === 0 ? p - firstX : p - firstY));
            const { x, y, points, startX, startY, segmentDistances, ...shapeData } = currentShape;
            const finalShape = {
                ...shapeData,
                points: relativePoints,
                segmentDistances: finalDistances,
                x: firstX,
                y: firstY,
            };

            setShapes(prevShapes => [...prevShapes, finalShape]);
            setIsDrawing(false);
            setCurrentShape(null);
        }
    }, [selectedTool, isDrawing, currentShape, setShapes, setCurrentShape, setIsDrawing, isRectangleMode]); // <-- added isRectangleMode

    return {
        handlePointerDown,
        handlePointerMove,
        handlePointerUp,
        handleDoubleClick,
    };
};